import React, { useState, useEffect } from 'react';
import Card from '../UI/Card';
import Button from '../UI/Button';
import Slider from '../UI/Slider';
import { useTranslation } from '../../hooks/useTranslation';
import { useSimulator } from '../../context/SimulatorContext';
import BUSINESS_CONFIG from '../../config/businessConfig';

// Note icon component
const NoteIcon = () => (
  <svg className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
  </svg>
);

export default function GenericLimitDetail({ 
  limitType, 
  onSave, 
  onBack 
}) {
  const { t } = useTranslation();
  const { state } = useSimulator();
  
  // Configuration for different limit types
  const configs = {
    netDeposit: {
      title: 'limits.net_deposit_detail.title',
      description: 'limits.net_deposit_detail.description',
      note: 'limits.net_deposit_detail.note',
      defaultLabel: 'limits.net_deposit_detail.default_label',
      defaultValue: 'limits.net_deposit_detail.default_value',
      sliders: [{
        key: 'limit',
        label: 'limits.net_deposit_detail.slider_label',
        min: BUSINESS_CONFIG.limits.netDeposit.min,
        max: BUSINESS_CONFIG.limits.netDeposit.max,
        step: BUSINESS_CONFIG.limits.netDeposit.step,
        initialValue: state.limits.netDeposit
      }],
      saveData: (values) => ({ netDeposit: values.limit })
    },
    deposit: {
      title: 'limits.deposit_detail.title',
      description: 'limits.deposit_detail.description',
      note: 'limits.deposit_detail.note',
      sliders: [
        {
          key: 'daily',
          label: 'limits.deposit_detail.daily_label',
          min: 10,
          max: 500,
          step: 10,
          initialValue: state.limits.deposit.daily
        },
        {
          key: 'weekly',
          label: 'limits.deposit_detail.weekly_label',
          min: 10,
          max: 1500,
          step: 50,
          initialValue: state.limits.deposit.weekly
        },
        {
          key: 'monthly',
          label: 'limits.deposit_detail.monthly_label',
          min: 10,
          max: 3000,
          step: 100,
          initialValue: state.limits.deposit.monthly
        }
      ],
      saveData: (values) => ({
        deposit: {
          daily: values.daily,
          weekly: values.weekly,
          monthly: values.monthly
        }
      }),
      enforceDWM: true
    },
    login: {
      title: 'limits.login_detail.title',
      description: 'limits.login_detail.description',
      note: 'limits.login_detail.note',
      sliders: [
        {
          key: 'daily',
          label: 'limits.login_detail.daily_label',
          min: 1,
          max: 8,
          step: 1,
          initialValue: state.limits.login.daily
        },
        {
          key: 'weekly',
          label: 'limits.login_detail.weekly_label',
          min: 1,
          max: 56,
          step: 1,
          initialValue: state.limits.login.weekly
        },
        {
          key: 'monthly',
          label: 'limits.login_detail.monthly_label',
          min: 1,
          max: 240,
          step: 5,
          initialValue: state.limits.login.monthly
        }
      ],
      saveData: (values) => ({
        login: {
          daily: values.daily,
          weekly: values.weekly,
          monthly: values.monthly
        }
      }),
      enforceDWM: true
    },
    withdrawal: {
      title: 'limits.withdrawal_detail.title',
      description: 'limits.withdrawal_detail.description',
      note: 'limits.withdrawal_detail.note',
      sliders: [
        {
          key: 'maxBalance',
          label: 'limits.withdrawal_detail.max_balance_label',
          min: 100,
          max: 2000,
          step: 50,
          initialValue: state.limits.withdrawal.maxBalance
        },
        {
          key: 'reserve',
          label: 'limits.withdrawal_detail.reserve_label',
          min: 0,
          max: 2000,
          step: 10,
          initialValue: state.limits.withdrawal.reserve
        }
      ],
      saveData: (values) => ({
        withdrawal: {
          maxBalance: values.maxBalance,
          reserve: values.reserve
        }
      }),
      enforceDependency: true
    }
  };

  const config = configs[limitType];
  
  // Initialize state for all sliders - always call useState
  const [values, setValues] = useState(() => {
    if (!config) return {};
    const initialValues = {};
    config.sliders.forEach(slider => {
      initialValues[slider.key] = slider.initialValue;
    });
    return initialValues;
  });

  // Enforce DWM logic (daily ≤ weekly ≤ monthly) - always call useEffect
  useEffect(() => {
    if (config?.enforceDWM) {
      const { daily, weekly, monthly } = values;
      if (daily > weekly) setValues(prev => ({ ...prev, weekly: daily }));
      if (weekly > monthly) setValues(prev => ({ ...prev, monthly: weekly }));
      if (weekly < daily) setValues(prev => ({ ...prev, daily: weekly }));
      if (monthly < weekly) setValues(prev => ({ ...prev, weekly: monthly }));
    }
  }, [values, config?.enforceDWM]);

  // Enforce dependency logic (reserve ≤ maxBalance) - always call useEffect
  useEffect(() => {
    if (config?.enforceDependency && values.reserve > values.maxBalance) {
      setValues(prev => ({ ...prev, reserve: values.maxBalance }));
    }
  }, [values.maxBalance, values.reserve, config?.enforceDependency]);

  // Early return if config is not found
  if (!config) {
    console.error(`Unknown limit type: ${limitType}`);
    return null;
  }

  // Update slider max values based on dependencies
  const getSliderConfig = (slider, index) => {
    let updatedSlider = { ...slider };
    
    if (config.enforceDWM && slider.key === 'weekly') {
      updatedSlider.min = values.daily;
    }
    if (config.enforceDWM && slider.key === 'monthly') {
      updatedSlider.min = values.weekly;
    }
    if (config.enforceDependency && slider.key === 'reserve') {
      updatedSlider.max = values.maxBalance;
    }
    
    return updatedSlider;
  };

  const handleSliderChange = (key, value) => {
    setValues(prev => ({ ...prev, [key]: Number(value) }));
  };

  const handleSave = () => {
    onSave(limitType, config.saveData(values));
  };

  return (
    <Card>
      {/* Heading */}
      <h3 className="text-2xl font-bold mb-2 text-center text-heading text-hc-black">
        {t(config.title)}
      </h3>

      {/* Description */}
      <div className="text-sm text-gray-700 mb-4">
        {t(config.description)}
      </div>

      {/* Default values (only for net deposit) */}
      {config.defaultLabel && (
        <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
          <div className="text-sm text-gray-700">
            <strong>{t(config.defaultLabel)}</strong> {t(config.defaultValue)}
          </div>
        </div>
      )}

      {/* Sliders */}
      <div className="space-y-4">
        {config.sliders.map((slider, index) => {
          const sliderConfig = getSliderConfig(slider, index);
          return (
            <Slider
              key={slider.key}
              min={sliderConfig.min}
              max={sliderConfig.max}
              step={sliderConfig.step}
              value={values[slider.key]}
              onChange={e => handleSliderChange(slider.key, e.target.value)}
              label={t(sliderConfig.label)}
            />
          );
        })}
      </div>

      {/* Note with icon */}
      <div className="flex gap-2 mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
        <NoteIcon />
        <span className="text-sm text-amber-800">
          {t(config.note)}
        </span>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3 mt-6">
        <Button variant="secondary" fullWidth onClick={onBack}>
          {t('common.back')}
        </Button>
        <Button fullWidth onClick={handleSave} variant="primary">
          {t('common.save')}
        </Button>
      </div>
    </Card>
  );
} 